import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function AboutPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToHome = () => {
    navigate('/');
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="px-8 lg:px-16 py-5">
          <div className="flex items-center justify-between">
            <button 
              onClick={handleBackToHome}
              className="text-xl font-serif tracking-wide hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer text-black"
            >
              SYG YACHTS
            </button>
            
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-2 text-xs tracking-wider hover:opacity-70 transition-opacity cursor-pointer text-black"
            >
              <i className="ri-arrow-left-line"></i>
              BACK TO HOME
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-8 lg:px-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="w-20 h-px bg-black mb-8"></div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-black mb-8 leading-tight">
            About SYG Yachts
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl leading-relaxed">
            A boutique superyacht brokerage and advisory firm delivering discreet expertise worldwide.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-black mb-8">Our Philosophy</h2>
              <div className="space-y-6 text-lg leading-relaxed text-gray-700">
                <p>
                  SYG Yachts is a boutique superyacht brokerage and advisory firm providing expert representation across yacht sales and acquisitions, charter, and comprehensive yacht services.
                </p>
                <p>
                  Our approach is informed by the standards, market intelligence, and transactional rigor of the world's leading superyacht houses, combined with the focus and agility of a private advisory.
                </p>
                <p>
                  We deliver senior-level involvement, strategic insight, and precision execution at every stage of ownership.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/a7be0bca81b21f9bc93301b4bdded2fd.jpg"
                alt="Luxury yacht interior"
                className="w-full h-[500px] object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* Values Section */}
          <div className="mb-24">
            <div className="w-16 h-px bg-black mb-8"></div>
            <h2 className="font-serif text-3xl md:text-4xl text-black mb-12">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="group">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg mb-6 group-hover:bg-black transition-colors">
                  <i className="ri-shield-check-line text-2xl text-black group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="font-serif text-xl mb-4">Discretion</h3>
                <p className="text-gray-600 leading-relaxed">
                  We understand the importance of privacy in high-value transactions. Every client relationship is handled with the utmost confidentiality.
                </p>
              </div>
              <div className="group">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg mb-6 group-hover:bg-black transition-colors">
                  <i className="ri-compass-3-line text-2xl text-black group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="font-serif text-xl mb-4">Expertise</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our team brings decades of combined experience in superyacht sales, charter, and management across global markets.
                </p>
              </div>
              <div className="group">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg mb-6 group-hover:bg-black transition-colors">
                  <i className="ri-hand-heart-line text-2xl text-black group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="font-serif text-xl mb-4">Dedication</h3>
                <p className="text-gray-600 leading-relaxed">
                  We are committed to exceeding expectations at every touchpoint, providing personalized service tailored to each client's unique needs.
                </p>
              </div>
            </div>
          </div>

          {/* Global Presence */}
          <div className="mb-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative order-2 lg:order-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20luxury%20marina%20with%20superyachts%20docked%20Mediterranean%20coastline%20azure%20blue%20water%20sunny%20day%20prestigious%20harbor&width=600&height=450&seq=about2&orientation=landscape"
                  alt="Luxury marina"
                  className="w-full h-[450px] object-cover rounded-lg shadow-lg"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="w-16 h-px bg-black mb-8"></div>
                <h2 className="font-serif text-3xl md:text-4xl text-black mb-8">Global Presence</h2>
                <div className="space-y-6 text-lg leading-relaxed text-gray-700">
                  <p>
                    With offices in Fort Lauderdale, Florida and Viareggio, Italy, we maintain a strategic presence in two of the world's most important yachting hubs.
                  </p>
                  <p>
                    Our international network allows us to serve clients across the Americas, Mediterranean, Caribbean, and beyond, with local expertise and global reach.
                  </p>
                </div>
                <div className="mt-8 grid grid-cols-2 gap-6">
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <i className="ri-map-pin-line text-2xl text-black mb-3"></i>
                    <h4 className="font-medium mb-1">Fort Lauderdale</h4>
                    <p className="text-sm text-gray-600">Florida, USA</p>
                  </div>
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <i className="ri-map-pin-line text-2xl text-black mb-3"></i>
                    <h4 className="font-medium mb-1">Viareggio</h4>
                    <p className="text-sm text-gray-600">Tuscany, Italy</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="bg-gray-900 rounded-2xl p-12 md:p-16 text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">Ready to Begin?</h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Whether you're looking to buy, sell, or charter a superyacht, our team is ready to provide the expert guidance you deserve.
            </p>
            <button 
              onClick={handleContactClick}
              className="px-10 py-4 bg-white text-black text-sm tracking-wider hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              CONTACT US
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
